import React, { Fragment, useEffect, useState } from 'react';
import { push } from 'react-router-redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage } from 'react-intl';

import Box from '@quintoandar/cozy-core/Box';
import Button from '@quintoandar/cozy-core/Button';
import WarningIcon from '@quintoandar/cozy-icons/Warning';

import ROUTES from 'routes/constants';
import PageWrapper from 'components/PageWrapper';
import AppBar from 'components/AppBar';
import DynamicForm from 'components/DynamicForm';
import { LoadingPage } from 'components/LoadingPage';

import useAssessment from 'containers/PropertyAssessment/hooks/useAssessment';
import { getUser } from 'utils/user';
import { IconDescription } from 'components/IconDescription';

import {
  checklistItemChanged,
  updateStorageChecklistItem,
  fetchChecklistAction,
  fetchChecklistFromStorageAction,
} from '../actions';

import {
  checklistHousePageViewedAnalytics,
  trackChecklistHouseFinishButtonClickedAction,
} from './actions';

import messages from './messages';
import { SECTIONS_TYPE } from '../constants';
import { selectChecklist } from '../selectors';
import { isChecklistStorageReady } from '../utils/checklistStorage';

export const House = ({
  params,
  goToChecklist,
  trackPageView,
  fetchChecklistFromStorage,
  onChangeItemChecklist,
  checklist,
  fetchChecklist,
  trackChecklistHouseFinishButtonClicked,
}) => {
  const [shouldValidate, setShouldValidate] = useState(false);
  const [isValid, setIsValid] = useState(true);

  useAssessment(params.id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* istanbul ignore next */
  useEffect(() => {
    if (!checklist.hasFetchedChecklistItems) {
      fetchChecklist();
    }

    trackPageView();
    fetchChecklistFromStorage({ userId: getUser().user_id });
  }, []);

  const user = getUser();

  const isNewChecklist = location.href.includes('new-checklist');

  const onValidate = isDynamicFormValid => {
    setIsValid(isDynamicFormValid);
    setShouldValidate(false);

    if (isDynamicFormValid) {
      goToChecklist(isNewChecklist);
    }
    trackChecklistHouseFinishButtonClicked({ isValid: isDynamicFormValid });
  };

  const validateForm = () => setShouldValidate(true);

  const errorMessage = (
    <IconDescription
      color="error"
      label={messages.invalid}
      icon={WarningIcon}
    />
  );

  const isFinished =
    checklist.hasFetchedChecklistItems &&
    isChecklistStorageReady(checklist);

  const ContentComponent = () => (
    <Fragment>
      <PageWrapper>
        {checklist.house !== undefined ? (
          <DynamicForm
            onChange={onChangeItemChecklist({
              sectionType: SECTIONS_TYPE.HOUSE,
              userId: user.user_id,
            })}
            sections={checklist.house}
            onValidate={onValidate}
            shouldValidate={shouldValidate}
          />
        ) : null}
        <Box my={4}>
          {!isValid && errorMessage}
          <Button fullWidth onClick={validateForm}>
            <FormattedMessage {...messages.finishButton} />
          </Button>
        </Box>
      </PageWrapper>
    </Fragment>
  );

  return (
    <Fragment>
      <AppBar
        // eslint-disable-next-line react/jsx-no-bind
        onBack={() => goToChecklist(isNewChecklist)}
        title={messages.title}
      />

      {isFinished ? ContentComponent() : <LoadingPage />}
    </Fragment>
  );
};

House.propTypes = {
  params: PropTypes.shape({
    id: PropTypes.string,
  }),
  goToChecklist: PropTypes.func,
  trackPageView: PropTypes.func,
  trackChecklistHouseFinishButtonClicked: PropTypes.func,
  onChangeItemChecklist: PropTypes.func,
  fetchChecklist: PropTypes.func,
  fetchChecklistFromStorage: PropTypes.func.isRequired,
  checklist: PropTypes.shape({
    showError: PropTypes.bool,
    progress: PropTypes.number,
  }),
};

export const mapStateToProps = state => ({
  checklist: selectChecklist(state),
});

export const mapDispatchToProps = (dispatch, ownProps) => ({
  goToChecklist: isNewChecklist =>
    dispatch(
      push(
        isNewChecklist
          ? ROUTES.NEW_CHECKLIST(ownProps.params.id)
          : ROUTES.CHECKLIST(ownProps.params.id),
      ),
    ),
  trackPageView: () =>
    dispatch(
      checklistHousePageViewedAnalytics({ photoSessionId: ownProps.params.id }),
    ),
  trackChecklistHouseFinishButtonClicked: ({ isValid }) =>
    dispatch(
      trackChecklistHouseFinishButtonClickedAction({
        photoSessionId: ownProps.params.id,
        isValid,
      }),
    ),
  fetchChecklist: () => dispatch(fetchChecklistAction()),
  fetchChecklistFromStorage: ({ userId }) =>
    dispatch(
      fetchChecklistFromStorageAction({
        userId,
        photoSessionId: ownProps.params.id,
      }),
    ),
  onChangeItemChecklist: ({ sectionType, userId }) => ({
    section,
    itemSlug,
    value,
    previousValue,
  }) => {
    dispatch(
      checklistItemChanged({
        photoSessionId: ownProps.params.id,
        sectionType,
        section,
        itemSlug,
        value,
        previousValue,
      }),
    );
    dispatch(
      updateStorageChecklistItem({
        sectionType,
        section,
        itemSlug,
        value,
        previousValue,
        photoSessionId: ownProps.params.id,
        userId,
      }),
    );
  },
});

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(House);
